import { AxiosResponse } from 'axios';
import { api } from 'src/common/utils';

import { CLOSED_STATUSES } from './constants';
import {
  AssignableUser,
  CreateRequestPayload,
  PaginatedResponse,
  ProvisioningRequest,
  ProvisioningRequestRow,
  QueueFilters,
  RequestStatus,
} from './types';

const ROOT_PATH = '/provisioning/requests';

export const buildQueueQueryString = (filters: QueueFilters, page: number): string => {
  const params = new URLSearchParams({ page: String(page) });

  if (filters.search) params.set('search', filters.search);
  if (filters.requestType) params.set('request_type', filters.requestType);

  if (filters.status) {
    params.set('status', filters.status);
  } else if (!filters.showClosed) {
    // Default view is what the manager still owes someone. An explicit status
    // filter wins, since picking "Completed" while hiding closed rows would
    // always come back empty.
    params.set('is_open', 'true');
  }

  return params.toString();
};

export const fetchRequests = (
  _key: string,
  filters: QueueFilters,
  page: number
): Promise<AxiosResponse<PaginatedResponse<ProvisioningRequestRow>>> =>
  api.get(`${ROOT_PATH}?${buildQueueQueryString(filters, page)}`);

export const fetchRequest = (
  _key: string,
  requestId: string
): Promise<AxiosResponse<ProvisioningRequest>> => api.get(`${ROOT_PATH}/${requestId}`);

export const fetchAssignableUsers = (): Promise<AxiosResponse<AssignableUser[]>> =>
  api.get(`${ROOT_PATH}/assignable_users`);

export const createRequest = (
  payload: CreateRequestPayload
): Promise<AxiosResponse<ProvisioningRequest>> => api.post(ROOT_PATH, payload);

export const updateRequestStatus = (
  requestId: string,
  status: RequestStatus,
  message: string
): Promise<AxiosResponse<ProvisioningRequest>> =>
  api.post(`${ROOT_PATH}/${requestId}/status`, { status, message });

export const assignRequest = (
  requestId: string,
  assignee: number | null
): Promise<AxiosResponse<ProvisioningRequest>> =>
  api.post(`${ROOT_PATH}/${requestId}/assign`, { assignee });

export const commentOnRequest = (
  requestId: string,
  message: string
): Promise<AxiosResponse<ProvisioningRequest>> =>
  api.post(`${ROOT_PATH}/${requestId}/comment`, { message });

export const uploadPurchaseOrder = (
  requestId: string,
  file: File
): Promise<AxiosResponse<ProvisioningRequest>> => {
  const formData = new FormData();
  formData.append('purchase_order_file', file);
  // No explicit Content-Type: axios derives it from the FormData body, and
  // setting it by hand drops the `boundary=` parameter, which leaves DRF
  // unable to parse the multipart payload at all.
  return api.post(`${ROOT_PATH}/${requestId}/purchase_order`, formData);
};

export const requiresResolutionNote = (status: RequestStatus): boolean =>
  CLOSED_STATUSES.includes(status);
